import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useRef } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Layout } from "../components/Layout";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { CustomerLogo } from "../components/CustomerLogo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, User, Trash2, Pencil, Upload, LogOut } from "lucide-react";

function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-US", { month: "short", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function SessionsTab() {
  const qc = useQueryClient();
  const { data: sessions } = useQuery({
    queryKey: ["sessions"],
    queryFn: async () => (await api.get("/admin/sessions?active=true")).data,
  });
  const revoke = useMutation({
    mutationFn: async (sid) => (await api.delete(`/admin/sessions/${sid}`)).data,
    onSuccess: () => { toast.success("Session revoked"); qc.invalidateQueries({ queryKey: ["sessions"] }); },
    onError: () => toast.error("Revoke failed"),
  });

  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        Active sign-in sessions stored in the webapp database. Revoke a session to end it (the user's next request re-authenticates).
      </p>
      <div className="rounded-md border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>IP Address</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Last Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions?.map((s) => (
              <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                <TableCell className="font-mono text-xs">{s.email}</TableCell>
                <TableCell className="text-sm">{s.role}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{s.ip_address || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{fmtWhen(s.created_at)}</TableCell>
                <TableCell className="font-mono text-xs">{fmtWhen(s.last_active_at)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" className="gap-1.5" data-testid={`revoke-session-${s.id}`} onClick={() => revoke.mutate(s.id)}>
                    <LogOut size={14} className="text-neg" /> Revoke
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {sessions?.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-muted-foreground">No active sessions.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function UserForm({ customers, initial, onSubmit, submitting }) {
  const [email, setEmail] = useState(initial?.email || "");
  const [name, setName] = useState(initial?.name || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(initial?.role || "user");
  const [access, setAccess] = useState(initial?.client_access || []);

  const toggle = (guid) =>
    setAccess((a) => (a.includes(guid) ? a.filter((x) => x !== guid) : [...a, guid]));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input data-testid="user-email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!initial} />
        </div>
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input data-testid="user-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>{initial ? "New password (optional)" : "Password"}</Label>
          <Input data-testid="user-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="user">User (scoped)</SelectItem>
              <SelectItem value="admin">Admin (all customers)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {role === "user" && (
        <div className="space-y-2">
          <Label>Customer access</Label>
          <div className="space-y-2 rounded-md border border-border p-3 max-h-48 overflow-auto">
            {customers?.map((c) => (
              <label key={c.payroll_client_GUID} className="flex items-center gap-2.5 cursor-pointer">
                <Checkbox
                  checked={access.includes(c.payroll_client_GUID)}
                  onCheckedChange={() => toggle(c.payroll_client_GUID)}
                  data-testid={`access-${c.client_short_name}`}
                />
                <span className="text-sm">{c.client_name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      <DialogFooter>
        <Button
          data-testid="user-save"
          onClick={() => onSubmit({ email, name, password, role, client_access: role === "admin" ? [] : access })}
          disabled={submitting}
        >
          {submitting ? "Saving…" : "Save user"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const { data: users } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/admin/users")).data });
  const { data: customers } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const nameByGuid = Object.fromEntries((customers || []).map((c) => [c.payroll_client_GUID, c.client_short_name]));

  const create = useMutation({
    mutationFn: async (body) => (await api.post("/admin/users", body)).data,
    onSuccess: () => { toast.success("User created"); setAddOpen(false); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const update = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/admin/users/${id}`, body)).data,
    onSuccess: () => { toast.success("User updated"); setEditUser(null); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/users/${id}`)).data,
    onSuccess: () => { toast.success("User removed"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="gap-2"><UserPlus size={16} /> Add user</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
            <UserForm customers={customers} onSubmit={(body) => create.mutate(body)} submitting={create.isPending} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Customer Access</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users?.map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <div className="font-medium">{u.name || "—"}</div>
                  <div className="font-mono text-xs text-muted-foreground">{u.email}</div>
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5 text-sm">
                    {u.role === "admin" ? <ShieldCheck size={15} className="text-warn" /> : <User size={15} className="text-muted-foreground" />}
                    {u.role}
                  </span>
                </TableCell>
                <TableCell>
                  {u.role === "admin" ? (
                    <span className="text-sm text-muted-foreground">All customers</span>
                  ) : u.client_access?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {u.client_access.map((g) => (
                        <span key={g} className="rounded-sm border border-border bg-muted px-2 py-0.5 text-xs font-mono">
                          {nameByGuid[g] || "?"}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">None</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Dialog open={editUser?.id === u.id} onOpenChange={(o) => setEditUser(o ? u : null)}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`edit-user-${u.email}`}><Pencil size={15} /></Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Edit {u.email}</DialogTitle></DialogHeader>
                        <UserForm
                          customers={customers}
                          initial={u}
                          submitting={update.isPending}
                          onSubmit={(body) => update.mutate({ id: u.id, body })}
                        />
                      </DialogContent>
                    </Dialog>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-user-${u.email}`}
                      onClick={() => remove.mutate(u.id)}
                    >
                      <Trash2 size={15} className="text-neg" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CustomerRow({ c }) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [bust, setBust] = useState(Date.now());
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/customers/${c.payroll_client_GUID}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Logo updated for ${c.client_short_name}`);
      setBust(Date.now());
      qc.invalidateQueries({ queryKey: ["clients"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      await api.delete(`/admin/customers/${c.payroll_client_GUID}/logo`);
      toast.success("Logo removed");
      setBust(Date.now());
      qc.invalidateQueries({ queryKey: ["clients"] });
    } catch (err) {
      toast.error("Remove failed");
    }
  };

  return (
    <TableRow>
      <TableCell>
        <CustomerLogo clientId={c.payroll_client_GUID} hasLogo={c.has_logo} size={48} iconSize={22} bust={bust} />
      </TableCell>
      <TableCell>
        <div className="font-medium">{c.client_name}</div>
        <div className="font-mono text-xs text-muted-foreground">{c.country_code} · {c.default_currency_code}</div>
      </TableCell>
      <TableCell className="text-right">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
          className="hidden"
          onChange={onFile}
          data-testid={`logo-input-${c.client_short_name}`}
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            data-testid={`upload-logo-${c.client_short_name}`}
          >
            <Upload size={14} /> {uploading ? "Uploading…" : c.has_logo ? "Replace" : "Upload logo"}
          </Button>
          {c.has_logo && (
            <Button variant="ghost" size="sm" onClick={removeLogo} data-testid={`remove-logo-${c.client_short_name}`}>
              <Trash2 size={14} className="text-neg" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

function CustomersTab() {
  const { data: customers } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        Upload a logo for each customer. Logos appear on the portfolio cards and customer pages. PNG, JPG, WEBP, GIF or SVG up to 3MB.
      </p>
      <div className="rounded-md border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-20">Logo</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {customers?.map((c) => <CustomerRow key={c.payroll_client_GUID} c={c} />)}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <Layout>
      <Breadcrumbs items={[{ label: "Portfolio", to: "/" }, { label: "Admin & Settings" }]} />
      <div className="mt-4">
        <h1 className="font-heading text-4xl font-black">Admin & Settings</h1>
        <p className="mt-1 text-muted-foreground">Manage users, customer access, and branding.</p>
      </div>

      <Tabs defaultValue="users" className="mt-6">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="users" data-testid="tab-users">Users & Access</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Customer Logos</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="customers" className="mt-6"><CustomersTab /></TabsContent>
        <TabsContent value="sessions" className="mt-6"><SessionsTab /></TabsContent>
      </Tabs>
    </Layout>
  );
}
